import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  type OnChanges,
  type OnInit,
  Output,
  type SimpleChanges,
} from '@angular/core';
import {
  type UsernameBadges,
  UsernameSize,
} from '@freelancer/components/username';
import {
  type ServiceOfferingShopCategoryService,
  ServiceOfferingShopCategoryServicePriceType as ServicePriceType,
  type ServiceOfferingWithOwnerDetails,
} from '@freelancer/datastore/collections';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkUnderline } from '@freelancer/ui/link';
import { PictureLayout, PictureObjectFit } from '@freelancer/ui/picture';
import { textVariants } from '@freelancer/ui/text';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import { CorporateStatusApi } from 'api-typings/users/users';
import { CategoryServiceActionOptions } from './service-offering-carousel.types';

@Component({
  standalone: false,
  selector: 'fl-service-offering-carousel-card',
  template: `
    @if (isLoading) {
      <div>
        <fl-loading-template class="LoadingCard" />
      </div>
    }
    @if (service && !isLoading) {
      <div class="ServiceCard">
        @if (isEditMode && isShopCategoryService) {
          <div class="Actions">
            <div>
              @if (serviceActionOptions.edit) {
                <fl-icon
                  class="mr-xxxsmall"
                  i18n-label="Edit service button text"
                  [name]="'ui-edit'"
                  [clickable]="true"
                  [clickAnimation]="'shrink'"
                  [color]="IconColor.LIGHT"
                  [hoverAnimation]="'none'"
                  label="Edit service"
                  [size]="IconSize.MID"
                  [useIconFont]="true"
                  (click)="triggerEditService($event, service)"
                ></fl-icon>
              }
            </div>
            <div class="Actions-right">
              @if (serviceActionOptions.remove) {
                <fl-icon
                  class="mr-xxxsmall"
                  i18n-label="Remove service button text"
                  [name]="'ui-subtract-circle'"
                  [clickable]="true"
                  [clickAnimation]="'shrink'"
                  [color]="IconColor.LIGHT"
                  [hoverAnimation]="'none'"
                  label="Remove service"
                  [size]="IconSize.MID"
                  [useIconFont]="true"
                  (click)="triggerRemoveService($event, service)"
                ></fl-icon>
              }
              @if (serviceActionOptions.toggleVisibility) {
                <fl-icon
                  [name]="'ui-visibility'"
                  [clickable]="true"
                  [clickAnimation]="'shrink'"
                  [color]="IconColor.LIGHT"
                  [hoverAnimation]="'none'"
                  [size]="IconSize.MID"
                  [useIconFont]="true"
                  (click)="triggerToggleVisibility($event, service)"
                ></fl-icon>
              }
              @if (serviceActionOptions.drag) {
                <fl-icon
                  class="mr-xxxsmall"
                  [name]="'ui-drag-indicator'"
                  [clickable]="true"
                  [clickAnimation]="'shrink'"
                  [color]="IconColor.LIGHT"
                  [hoverAnimation]="'none'"
                  [size]="IconSize.MID"
                  [useIconFont]="true"
                ></fl-icon>
              }
            </div>
          </div>
        }
        <fl-link
          flTrackingLabel="ViewService"
          [link]="serviceUrl"
          [queryParams]="fromUrl ? { from: fromUrl } : {}"
          [underline]="LinkUnderline.NEVER"
        >
          <div class="ImageContainer">
            <fl-picture
              class="ServiceCard-media mb-xxsmall"
              [class.IsEditable]="isEditMode"
              alt="Shopfront service card media"
              i18n-alt="Shopfront service card media"
              [alignCenter]="true"
              [borderRadiusBottom]="true"
              [borderRadiusTop]="true"
              [fixedAspectRatio]="true"
              [layout]="PictureLayout.RESPONSIVE"
              [src]="imageUrl"
              [sizes]="imageSizes"
              [width]="442"
              (imageLoaded)="isImageReady = true"
            >
            </fl-picture>
            @if (!isImageReady) {
              <fl-loading-text class="ImageLoading"></fl-loading-text>
            }
          </div>
          <p
            i18n="Service offering title"
            [class]="titleClasses"
            [maxHeight]="'max-content'"
            [flMaxLines]="1"
          >
            {{ service.title }}
          </p>
        </fl-link>
        <div class="ServiceCard-details">
          @if (username) {
            <fl-username
              class="ServiceCard-username"
              [isBold]="false"
              [link]="'/u/' + username"
              [username]="username"
              [badges]="userBadges"
              [size]="usernameSize"
              [sizeTablet]="usernameSizeTablet"
              [sizeDesktop]="usernameSizeDesktop"
              [truncateText]="true"
            >
            </fl-username>
          }
          @if (service?.priceType; as type) {
            @if (
              type === ServicePriceType.SUBSCRIPTION_ONLY_MONTHLY ||
              type === ServicePriceType.SUBSCRIPTION_MONTHLY_AND_HOURLY
            ) {
              <p
                [class]="priceClasses"
                i18n="Service offering price for monthly subscription"
              >
                {{
                  service.price
                    | flCurrency
                      : 'USD'
                      : { withPrecision: false, disableCode: true }
                }}
                / month
              </p>
            }
            @if (
              type === ServicePriceType.SUBSCRIPTION_ONLY_ANNUALLY ||
              type === ServicePriceType.SUBSCRIPTION_ANNUALLY_AND_HOURLY
            ) {
              <p
                [class]="priceClasses"
                i18n="Service offering price for yearly subscription"
              >
                {{
                  service.price
                    | flCurrency
                      : 'USD'
                      : { withPrecision: false, disableCode: true }
                }}
                / year
              </p>
            }
            @if (
              type === ServicePriceType.SUBSCRIPTION_ONLY_WEEKLY ||
              type === ServicePriceType.SUBSCRIPTION_WEEKLY_AND_HOURLY
            ) {
              <p
                [class]="priceClasses"
                i18n="Service offering price for weekly subscription"
              >
                {{
                  service.price
                    | flCurrency
                      : 'USD'
                      : { withPrecision: false, disableCode: true }
                }}
                / week
              </p>
            }
            @if (type === ServicePriceType.HOURLY_ONLY) {
              <div class="CardPriceContainer">
                <p
                  i18n="Service offering price for hourly"
                  [class]="priceClasses"
                  [class.line-through]="discountedPrice"
                  [class.mr-xxsmall]="discountedPrice"
                  [class.mr-xxxsmall]="!discountedPrice"
                >
                  {{
                    service.price
                      | flCurrency
                        : 'USD'
                        : { withPrecision: false, disableCode: true }
                  }}
                </p>
                @if (discountedPrice) {
                  <p
                    [class]="discountedPriceClasses"
                    [class.mr-xxxsmall]="true"
                    i18n="Service offering for hourly discounted price"
                  >
                    {{
                      discountedPrice
                        | flCurrency
                          : 'USD'
                          : { withPrecision: false, disableCode: true }
                    }}
                  </p>
                }
                <p
                  i18n="Service offering price for hourly"
                  [class]="priceClasses"
                >
                  / hour
                </p>
              </div>
            }
            @if (type === ServicePriceType.HAS_FIXED_ITEMS) {
              <div
                [class.CardPriceContainer]="
                  type === ServicePriceType.HAS_FIXED_ITEMS && discountedPrice
                "
              >
                <p
                  i18n="Service offering price"
                  [class]="priceClasses"
                  [class.line-through]="discountedPrice"
                  [class.mr-xxsmall]="discountedPrice"
                >
                  {{
                    service.price
                      | flCurrency
                        : 'USD'
                        : { withPrecision: false, disableCode: true }
                  }}
                </p>
                @if (discountedPrice) {
                  <p
                    [class]="discountedPriceClasses"
                    i18n="Service offering discounted price"
                  >
                    {{
                      discountedPrice
                        | flCurrency
                          : 'USD'
                          : { withPrecision: false, disableCode: true }
                    }}
                  </p>
                }
              </div>
            }
          }
        </div>
      </div>
    }
    @if (isEditMode && !service && !isLoading) {
      <div
        class="AddService"
        tabindex="0"
        flTrackingLabel="AddService"
        (click)="addService.emit()"
      >
        <fl-icon
          class="mb-xxsmall"
          [name]="'ui-add-circle'"
          [useIconFont]="true"
          [size]="IconSize.LARGE"
        ></fl-icon>
        <p
          class="text-body-16 desktop:text-body-16"
          i18n="Add a service offering"
        >
          Add a service
        </p>
      </div>
    }
    @if (showSeeMoreCard && !isEditMode && !isLoading) {
      <div
        tabindex="0"
        flTrackingLabel="SeeMoreServices"
      >
        <fl-link
          class="SeeMoreServices"
          flTrackingLabel="ViewService"
          [link]="'/freemarket'"
          [underline]="LinkUnderline.NEVER"
        >
          <fl-picture
            class="mb-xxxsmall"
            alt="See more service card media"
            i18n-alt="See more service card media"
            [alignCenter]="true"
            [borderRadiusBottom]="true"
            [borderRadiusTop]="true"
            [fixedAspectRatio]="true"
            [src]="seeMoreImageSrc"
            [width]="442"
          >
          </fl-picture>
          <p
            class="text-light text-body-20 SeeMoreServicesText desktop:text-body-24 font-bold"
            i18n="Service offering title"
          >
            See more services
          </p>
        </fl-link>
      </div>
    }
    @if (emptyCard && !isEditMode && !isLoading) {
      <div class="EmptyCategoryCard">
        <fl-picture
          alt="Empty category coming soon card media"
          i18n-alt="Empty category coming soon card media"
          class="EmptyCategoryPicture mb-xxsmall"
          [alignCenter]="true"
          [borderRadiusBottom]="true"
          [borderRadiusTop]="true"
          [fixedAspectRatio]="true"
          [objectFit]="PictureObjectFit.CONTAIN"
          [src]="emptyCategoryImageSrc"
          [width]="442"
        >
        </fl-picture>
        <p
          class="text-body-16 desktop:text-body-20"
          i18n="Coming soon text"
        >
          Coming Soon
        </p>
      </div>
    }
  `,
  styleUrls: ['./service-offering-carousel-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceOfferingCarouselCardComponent implements OnChanges, OnInit {
  IconColor = IconColor;
  IconSize = IconSize;
  LinkUnderline = LinkUnderline;
  PictureLayout = PictureLayout;
  PictureObjectFit = PictureObjectFit;
  ServicePriceType = ServicePriceType;

  @Input() service?:
    | ServiceOfferingShopCategoryService
    | ServiceOfferingWithOwnerDetails;
  @Input() emptyCard = false;
  @Input() isEditMode = false;
  @Input() isLoading = false;
  @Input() imageSizes?: string;
  @Input() serviceActionOptions: CategoryServiceActionOptions;
  @Input() titleSize = TextSize.BODY_16;
  @Input() titleSizeTablet = TextSize.BODY_16;
  @Input() titleSizeDesktop = TextSize.BODY_16;
  @Input() priceSize = TextSize.BODY_16;
  @Input() priceSizeTablet = TextSize.BODY_16;
  @Input() priceSizeDesktop = TextSize.BODY_16;
  @Input() usernameSize = UsernameSize.MID;
  @Input() usernameSizeTablet = UsernameSize.MID;
  @Input() usernameSizeDesktop = UsernameSize.LARGE;
  @Input() useSmallerPriceSize = false;
  @Input() fromUrl?: string;
  @Input() hideUsername = false;
  @Input() showSeeMoreCard = false;

  // FIXME: T300116 Enable hydration. Currently fails on client side with
  // TypeError: Cannot read properties of null (reading 'firstChild')
  @HostBinding('attr.ngSkipHydration') ngSkipHydration = true;

  userBadges?: UsernameBadges;
  username?: string;
  imageUrl: string;
  serviceUrl: string;
  isShopCategoryService: boolean;
  discountedPrice?: number;

  isImageReady = false;
  seeMoreImageSrc = 'services/see-more.png';
  emptyCategoryImageSrc = 'shopfronts/empty-category-box.png';

  priceClasses: string;
  discountedPriceClasses: string;
  titleClasses: string;

  @Output() addService = new EventEmitter<void>();
  @Output() editService =
    new EventEmitter<ServiceOfferingShopCategoryService>();
  @Output() toggleVisibility =
    new EventEmitter<ServiceOfferingShopCategoryService>();
  @Output() removeService =
    new EventEmitter<ServiceOfferingShopCategoryService>();

  ngOnChanges(changes: SimpleChanges): void {
    if ('service' in changes && this.service !== undefined) {
      if (this.isShopCategoryServiceType(this.service)) {
        this.isShopCategoryService = true;
        this.serviceUrl = `/service/${
          this.service.seoUrl ?? this.service.serviceOfferingId
        }`;
        this.imageUrl = this.service.heroImageUrl ?? '';
        this.username = undefined;
        this.userBadges = undefined;
      }

      if (this.isServiceOfferingWithOwnerDetailsType(this.service)) {
        this.isShopCategoryService = false;
        this.serviceUrl = `/service/${this.service.seoUrl ?? this.service.id}`;
        this.imageUrl = this.service.attachments?.[0]?.downloadable_url ?? '';
        this.username = this.service.owner.username;
        this.userBadges = {
          corporate:
            this.service.owner.corporate?.status === CorporateStatusApi.ACTIVE,
          preferred_freelancer: this.service.owner.preferredFreelancer,
          verified: this.service.owner.status.freelancerVerifiedUser,
        };
      }

      // Check for discount validity and set the discounted price here
      // return undefined if discount validity is over or not set
      this.discountedPrice =
        this.service?.discountDetails &&
        Date.now() <= this.service.discountDetails.validUntil &&
        Date.now() >= this.service.discountDetails.validFrom
          ? this.service.discountDetails.discountedPrice
          : undefined;
    }

    this.computeClasses();
  }

  ngOnInit(): void {
    if (this.useSmallerPriceSize) {
      this.priceSize = TextSize.BODY_16;
      this.priceSizeTablet = TextSize.BODY_16;
      this.priceSizeDesktop = TextSize.BODY_16;
    }
    this.computeClasses();
  }

  private computeClasses(): void {
    this.titleClasses = textVariants({
      size: this.titleSize,
      sizeDesktop: this.titleSizeDesktop,
    });

    this.priceClasses = textVariants({
      size: this.priceSize,
      sizeDesktop: this.priceSizeDesktop,
      weight: FontWeight.MEDIUM,
    });

    this.discountedPriceClasses = textVariants({
      size: this.priceSize,
      sizeDesktop: this.priceSizeDesktop,
      weight: FontWeight.BLACK,
    });
  }

  isShopCategoryServiceType(
    service:
      | ServiceOfferingShopCategoryService
      | ServiceOfferingWithOwnerDetails,
  ): service is ServiceOfferingShopCategoryService {
    return 'categoryId' in service;
  }

  isServiceOfferingWithOwnerDetailsType(
    service:
      | ServiceOfferingShopCategoryService
      | ServiceOfferingWithOwnerDetails,
  ): service is ServiceOfferingWithOwnerDetails {
    return 'status' in service;
  }

  // Stop propagation of icon click events, since the service card itself may become clickable in the future
  triggerEditService(
    event: MouseEvent,
    service:
      | ServiceOfferingShopCategoryService
      | ServiceOfferingWithOwnerDetails,
  ): void {
    if (this.isShopCategoryServiceType(service)) {
      event.stopPropagation();
      this.editService.emit(service);
    }
  }

  triggerToggleVisibility(
    event: MouseEvent,
    service:
      | ServiceOfferingShopCategoryService
      | ServiceOfferingWithOwnerDetails,
  ): void {
    if (this.isShopCategoryServiceType(service)) {
      event.stopPropagation();
      this.toggleVisibility.emit(service);
    }
  }

  triggerRemoveService(
    event: MouseEvent,
    service:
      | ServiceOfferingShopCategoryService
      | ServiceOfferingWithOwnerDetails,
  ): void {
    if (this.isShopCategoryServiceType(service)) {
      event.stopPropagation();
      this.removeService.emit(service);
    }
  }
}
